import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'localization';
import ButtonAtom from '../../atoms/Button/ButtonAtom';
import StatusBadgeAtom from '../../atoms/StatusBadge/StatusBadgeAtom';
import './WaitlistGateOrganism.scss';

export type WaitlistGateStatus = 'pending' | 'approved' | 'rejected';

export interface WaitlistGateOrganismProps {
  /**
   * Where the signed-in person stands on the waitlist, as `/auth/bootstrap` reports it.
   * `null` means they belong to no organization and have no waitlist entry either.
   */
  status: WaitlistGateStatus | null;
  /** The email the waitlist request is filed under; named in the pending state when known. */
  email?: string | null;
  /**
   * With `status='approved'`: the organization accepts this person's domain but their email is
   * not verified, so they cannot join yet. Shows the verify-your-email state.
   */
  verificationRequired?: boolean;
  /** True while a re-check is running: the primary action shows its busy state. */
  checking?: boolean;
  /** Refreshes the session and asks the server again. */
  onCheckAgain: () => void;
  onSignOut: () => void;
  /** The Center logo shown above the heading; omitted when the host supplies none. */
  logoSrc?: string;
}

/**
 * Shown to a person who has signed in but belongs to no organization. It tells them where
 * they stand and gives them the two ways forward: look again, or sign out. The rejected
 * state offers only sign out, since there is nothing to wait for.
 */
const WaitlistGateOrganism: React.FC<WaitlistGateOrganismProps> = ({
  status,
  email = null,
  verificationRequired = false,
  checking = false,
  onCheckAgain,
  onSignOut,
  logoSrc,
}) => {
  const { t } = useTranslation();
  const headingRef = useRef<HTMLHeadingElement>(null);

  // The whole view changes with the status, so focus returns to the heading that names it.
  useEffect(() => {
    headingRef.current?.focus();
  }, [status, verificationRequired]);

  const content = (() => {
    switch (status) {
      case 'pending':
        return {
          title: t('waitlist.gate.pending.title'),
          messages: [
            t('waitlist.gate.pending.thanks'),
            t('waitlist.gate.pending.next'),
            ...(email ? [t('waitlist.gate.pending.emailNote', { email })] : []),
          ],
          badge: <StatusBadgeAtom status="pending" label={t('waitlist.status.pending.badge')} showIcon />,
          primaryLabel: t('waitlist.gate.checkAgain'),
        };
      case 'approved':
        if (verificationRequired) {
          return {
            title: t('waitlist.gate.verificationRequired.title'),
            messages: [
              email
                ? t('waitlist.gate.verificationRequired.message', { email })
                : t('waitlist.gate.verificationRequired.messageNoEmail'),
              t('waitlist.gate.verificationRequired.next'),
            ],
            badge: (
              <StatusBadgeAtom
                status="pending"
                label={t('waitlist.gate.verificationRequired.badge')}
                showIcon
              />
            ),
            primaryLabel: t('waitlist.gate.checkAgain'),
          };
        }
        return {
          title: t('waitlist.gate.approved.title'),
          messages: [t('waitlist.gate.approved.message')],
          badge: <StatusBadgeAtom status="active" label={t('waitlist.status.approved.badge')} showIcon />,
          primaryLabel: t('waitlist.gate.approved.continue'),
        };
      case 'rejected':
        return {
          title: t('waitlist.gate.rejected.title'),
          messages: [t('waitlist.status.rejected.message'), t('waitlist.status.rejected.contactSupport')],
          badge: <StatusBadgeAtom status="error" label={t('waitlist.status.rejected.badge')} showIcon />,
          primaryLabel: null,
        };
      default:
        return {
          title: t('appInit.noOrganizationTitle'),
          messages: [t('appInit.noOrganizationDescription')],
          badge: null,
          primaryLabel: t('waitlist.gate.checkAgain'),
        };
    }
  })();

  return (
    <main className="waitlist-gate">
      <section
        className="waitlist-gate__panel"
        aria-labelledby="waitlist-gate-heading"
        data-testid={`waitlist-gate-${verificationRequired && status === 'approved' ? 'verification-required' : (status ?? 'unknown')}`}
      >
        {logoSrc && <img src={logoSrc} alt={t('common.logo.alt')} className="waitlist-gate__logo" />}
        {(content.badge || checking) && (
          <output className="waitlist-gate__status">
            {checking ? t('waitlist.gate.checking') : content.badge}
          </output>
        )}
        <h1
          id="waitlist-gate-heading"
          ref={headingRef}
          tabIndex={-1}
          className="waitlist-gate__title"
        >
          {content.title}
        </h1>
        {content.messages.map((message) => (
          <p key={message} className="waitlist-gate__message">
            {message}
          </p>
        ))}
        <div className="waitlist-gate__actions">
          {content.primaryLabel && (
            <ButtonAtom
              label={checking ? t('waitlist.gate.checking') : content.primaryLabel}
              variant="primary"
              loading={checking}
              aria-busy={checking}
              onClick={onCheckAgain}
              data-testid="waitlist-gate-check-again"
            />
          )}
          <ButtonAtom
            label={t('waitlist.gate.signOut')}
            variant={content.primaryLabel ? 'secondary' : 'primary'}
            onClick={onSignOut}
            data-testid="waitlist-gate-sign-out"
          />
        </div>
      </section>
    </main>
  );
};

export default WaitlistGateOrganism;
